/**
 * Resolve a slice's world position for Cornerstone `jumpToWorld` from
 * Cornerstone's own per-imageId `imagePlaneModule` — i.e. the real
 * ImagePositionPatient the volume was actually built from (loaded from the
 * DICOM header via the wadouri loader).
 *
 * Why this source and not the obvious ones:
 *   - `studyMetadata.image_position_patient` is absent on most canonical
 *     studies (the sidecar carries only a scalar `z_position`).
 *   - `z_position` can run OPPOSITE to the real IPP projection on
 *     plane-aware-reversed coronal/sagittal reformat series (the canonical
 *     pipeline reverses their slice order), so a z-derived world point lands
 *     on the mirror slice.
 *   - `jumpToSlice({ imageIndex })` treats the slot list-index as a Cornerstone
 *     K-step, which is the geometric (IPP-sorted) order — also mirrored on
 *     those volumes.
 *
 * Reading the per-slice IPP Cornerstone holds keeps the write/jump path in the
 * same real-geometry frame as the K-step→list read-back (`kStepToListIndex`,
 * #401), so the slider thumb and the rendered slice agree on every plane.
 */

/** Minimal shape of Cornerstone's metadata registry this depends on. */
export interface CornerstoneMetaDataLike {
  get: (type: string, imageId: string) => unknown;
}

export function sliceWorldPositionFromMetadata(
  metaData: CornerstoneMetaDataLike,
  imageId: string | undefined,
): [number, number, number] | null {
  if (!imageId) return null;
  const mod = metaData.get("imagePlaneModule", imageId) as
    | { imagePositionPatient?: number[] | null }
    | undefined;
  const ipp = mod?.imagePositionPatient;
  if (!ipp || ipp.length < 3) return null;
  const [x, y, z] = ipp;
  if (typeof x !== "number" || typeof y !== "number" || typeof z !== "number") return null;
  return [x, y, z];
}
